import {useState, useEffect, useRef, useCallback} from 'react';

interface UseTemporaryVisibilityOptions {
  /** Duration in milliseconds before auto-hiding. Default is 5000ms (5 seconds) */
  duration?: number;
}

/**
 * Hook for managing temporary visibility of sensitive data with auto-hide functionality.
 * Useful for showing sensitive information (like emails, passwords) in table columns
 * that automatically hides after a fixed duration.
 *
 * @param options - Configuration options
 * @returns Object containing `isVisible` check function and `toggleVisibility` toggle function
 *
 * @example
 * ```tsx
 * const {isVisible, toggleVisibility} = useTemporaryVisibility({duration: 5000});
 *
 * // In a table column render:
 * const isVisible = visibleEmails.has(row.id);
 * return !isVisible ? (
 *   <button onClick={() => toggleVisibility(row.id)}>Show</button>
 * ) : (
 *   <span onClick={() => toggleVisibility(row.id)}>{row.email}</span>
 * );
 * ```
 */
export function useTemporaryVisibility(options: UseTemporaryVisibilityOptions = {}) {
  const {duration = 5000} = options;
  const [visibleItems, setVisibleItems] = useState<Set<number | string>>(new Set());
  const timeoutsRef = useRef<Map<number | string, NodeJS.Timeout>>(new Map());

  /**
   * Check if an item is currently visible
   */
  const isVisible = useCallback(
    (id: number | string): boolean => {
      return visibleItems.has(id);
    },
    [visibleItems]
  );

  /**
   * Toggle visibility of an item. If showing, it will auto-hide after the duration.
   */
  const toggleVisibility = useCallback(
    (id: number | string) => {
      setVisibleItems(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          // User manually hiding - clear the timeout if it exists
          const timeout = timeoutsRef.current.get(id);
          if (timeout) {
            clearTimeout(timeout);
            timeoutsRef.current.delete(id);
          }
          newSet.delete(id);
        } else {
          // Showing the item - set auto-hide timeout
          newSet.add(id);
          const timeout = setTimeout(() => {
            setVisibleItems(current => {
              const updatedSet = new Set(current);
              updatedSet.delete(id);
              return updatedSet;
            });
            timeoutsRef.current.delete(id);
          }, duration);
          timeoutsRef.current.set(id, timeout);
        }
        return newSet;
      });
    },
    [duration]
  );

  /**
   * Clear all visible items and their timeouts
   */
  const clearAll = useCallback(() => {
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current.clear();
    setVisibleItems(new Set());
  }, []);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  return {
    isVisible,
    toggleVisibility,
    clearAll,
    /** Internal state - exposed for advanced use cases */
    visibleItems,
  };
}